import type { Task, TaskStav } from './api';

const SHORT_DATE = new Intl.DateTimeFormat('cs-CZ', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

export function formatTaskDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return iso ?? '—';
  return SHORT_DATE.format(new Date(ts));
}

/** Reminder time as HH:MM, or null when absent / not a time. */
function normalizeCas(cas: string | null | undefined): string | null {
  const m = (cas ?? '').match(/^(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : null;
}

/**
 * Date + optional time-of-day for display: "21.05.2026" for a whole-day entry,
 * "21.05.2026 14:30" when a time is set, "—" when undated. Shared by task
 * reminders and planned contacts.
 */
export function formatTermin(iso: string | null | undefined, cas?: string | null): string {
  const date = formatTaskDate(iso);
  const hhmm = iso ? normalizeCas(cas) : null;
  return hhmm ? `${date} ${hhmm}` : date;
}

export function formatTaskTermin(
  t: Pick<Task, 'datum_pripominky'> & Partial<Pick<Task, 'cas_pripominky'>>,
): string {
  return formatTermin(t.datum_pripominky, t.cas_pripominky);
}

/** Local timestamp of a reminder with a time-of-day (date-only → null). */
function reminderInstant(iso: string, cas: string | null | undefined): number | null {
  const hhmm = normalizeCas(cas);
  if (!hhmm) return null;
  const ts = new Date(`${iso.slice(0, 10)}T${hhmm}:00`).getTime();
  return Number.isNaN(ts) ? null : ts;
}

/**
 * True when a reminder is overdue. Whole-day reminders are overdue only once
 * the day is safely behind us (more than 24h in the past); reminders with a
 * time-of-day are overdue as soon as that local time has passed.
 */
export function isPastDue(iso: string | null | undefined, cas?: string | null): boolean {
  if (!iso) return false;
  const at = reminderInstant(iso, cas);
  if (at != null) return at < Date.now();
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return false;
  return ts < Date.now() - 24 * 60 * 60 * 1000;
}

export function isThisWeek(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return false;
  const now = Date.now();
  const weekMs = 7 * 24 * 60 * 60 * 1000;
  return ts >= now - 24 * 60 * 60 * 1000 && ts <= now + weekMs;
}

/**
 * Tone for the state `Marker`:
 *   aktivní  → warn (amber)  — not acknowledged yet, needs attention
 *   přijatý  → accent (blue) — acknowledged, in progress
 *   splněný  → ok (green)
 *   zrušený  → neutral (gray)
 * (overdue is flagged separately with its own amber "Po termínu" marker)
 */
export function stavTone(
  stav: TaskStav | string | null | undefined,
): 'accent' | 'ok' | 'warn' | 'neutral' {
  if (stav === 'splneny') return 'ok';
  if (stav === 'zruseny') return 'neutral';
  if (stav === 'prijato') return 'accent';
  return 'warn';
}

export function stavLabel(stav: TaskStav | string | null | undefined): string {
  if (stav === 'splneny') return 'Splněný';
  if (stav === 'zruseny') return 'Zrušený';
  if (stav === 'prijato') return 'Přijatý';
  return 'Aktivní';
}

/** Open = not yet done/cancelled (aktivní or přijato). */
export function isOpenStav(stav: TaskStav | string | null | undefined): boolean {
  return stav === 'aktivni' || stav === 'prijato';
}

export function taskTitle(t: Pick<Task, 'nazev'>): string {
  return t.nazev?.trim() || '—';
}

/** YYYY-MM-DD for a date input, from any ISO/date string. */
export function toDateInputValue(iso: string | null | undefined): string {
  if (!iso) return '';
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return '';
  const d = new Date(ts);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/** HH:MM for a time input, from an API HH:MM / HH:MM:SS value. */
export function toTimeInputValue(cas: string | null | undefined): string {
  return normalizeCas(cas) ?? '';
}

/**
 * Live mask for the 24h time text field (we deliberately avoid the native
 * `<input type="time">` — it renders in the *browser's* locale, so an English
 * browser shows AM/PM in a Czech product). Keeps digits only, inserts the
 * colon after the hour, caps at HH:MM.
 */
export function maskTimeInput(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
}

/**
 * Final normalisation on blur/submit: "9", "9:5", "930", "09:30" → "09:30";
 * anything not a valid 24h time → "" (whole day).
 */
export function normalizeTimeInput(raw: string): string {
  const s = raw.trim();
  if (!s) return '';
  let h: string;
  let m: string;
  const colon = s.match(/^(\d{1,2}):(\d{0,2})$/);
  if (colon) {
    h = colon[1];
    m = colon[2];
  } else if (/^\d{1,4}$/.test(s)) {
    // "9" → 09:00, "930" → 09:30, "0930" / "1430" → as written.
    const d = s.length === 3 ? `0${s}` : s;
    h = d.slice(0, 2);
    m = d.length > 2 ? d.slice(2) : '';
  } else {
    return '';
  }
  const hh = Number(h);
  const mm = Number(m || '0');
  if (!Number.isInteger(hh) || !Number.isInteger(mm) || hh > 23 || mm > 59) return '';
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

export function todayInputValue(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}
